import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';
import {
  AdminStatsGrid,
  GoldRateManagement,
  ReferralSettings,
  DailyReportSettings,
  AdminQuickLinks,
  TodaysActivity,
  DangerZone,
  ViewOnlyNotice,
  SIPDashboardCard
} from '../../components/admin';

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminPermissions, setAdminPermissions] = useState(null);
  
  // Gold rate override state
  const [overrideRate, setOverrideRate] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);
  
  // Referral settings state
  const [referralSettings, setReferralSettings] = useState(null);
  const [newReferralReward, setNewReferralReward] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  
  // Daily report state
  const [dailyReportEmails, setDailyReportEmails] = useState(['', '', '']);
  const [dailyReportLoading, setDailyReportLoading] = useState(false);
  const [dailyReportEnabled, setDailyReportEnabled] = useState(false);
  const [sendingTestReport, setSendingTestReport] = useState(false);
  
  // Delete data state
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const accessRes = await adminAPI.checkAccess();
      if (!accessRes.data.is_admin) {
        toast.error('Admin access required');
        navigate('/home-new');
        return;
      }
      setAdminPermissions(accessRes.data);
      
      const permissions = accessRes.data.permissions || {};
      
      if (!permissions.dashboard && !accessRes.data.is_primary_admin) {
        if (permissions.transactions) {
          navigate('/admin/transactions');
          return;
        } else if (permissions.users) {
          navigate('/admin/users');
          return;
        } else if (permissions.reports) {
          navigate('/admin/reports');
          return;
        } else {
          toast.error('No admin sections available');
          navigate('/home-new');
          return;
        }
      }
      
      const promises = [adminAPI.getDashboard()];
      
      if (accessRes.data.permissions?.settings || accessRes.data.is_primary_admin) {
        promises.push(adminAPI.getReferralSettings());
        promises.push(adminAPI.getDailyReportSettings());
      }
      
      const results = await Promise.all(promises);
      setDashboard(results[0].data);
      
      if (results[1]) {
        setReferralSettings(results[1].data);
        // Use reward_inr if available, fallback to reward_millis for backward compat
        const rewardValue = results[1].data?.reward_inr || results[1].data?.reward_millis || 50;
        setNewReferralReward(rewardValue.toString());
      }
      
      if (results[2]) {
        const emails = results[2].data?.emails || [];
        const paddedEmails = [...emails, '', '', ''].slice(0, 3);
        setDailyReportEmails(paddedEmails);
        setDailyReportEnabled(results[2].data?.enabled || false);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Admin access required');
        navigate('/home-new');
      } else {
        toast.error('Failed to load admin dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Permission helpers
  const hasPermission = (section) => {
    if (loading || !adminPermissions) return true;
    if (adminPermissions.is_primary_admin) return true;
    return adminPermissions.permissions?.[section] ?? false;
  };

  const canEdit = () => {
    if (loading || !adminPermissions) return true;
    if (adminPermissions.is_primary_admin) return true;
    return !adminPermissions.permissions?.view_only;
  };

  const isPrimaryAdmin = () => {
    if (loading || !adminPermissions) return true;
    return adminPermissions.is_primary_admin;
  };

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Handler functions
  const handleOverrideRate = async () => {
    if (!overrideRate || parseFloat(overrideRate) <= 0) {
      toast.error('Please enter a valid rate');
      return;
    }
    if (!overrideReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    setOverrideLoading(true);
    try {
      await adminAPI.overrideRate(parseFloat(overrideRate), overrideReason);
      toast.success('Gold rate overridden successfully');
      await fetchDashboard();
      setOverrideRate('');
      setOverrideReason('');
    } catch (error) {
      toast.error('Failed to override rate');
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleClearOverride = async () => {
    setOverrideLoading(true);
    try {
      await adminAPI.clearRateOverride();
      toast.success('Rate override cleared');
      await fetchDashboard();
    } catch (error) {
      toast.error('Failed to clear override');
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleDeleteAllData = async () => {
    setDeleteLoading(true);
    try {
      const response = await adminAPI.deleteAllData();
      toast.success(`All data deleted: ${response.data.deleted.users} users, ${response.data.deleted.transactions} transactions`);
      await fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete data');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUpdateReferralReward = async () => {
    const reward = parseFloat(newReferralReward);
    if (isNaN(reward) || reward < 0) {
      toast.error('Please enter a valid reward amount');
      return;
    }
    if (reward > 500) {
      toast.error('Reward cannot exceed ₹500');
      return;
    }

    setReferralLoading(true);
    try {
      await adminAPI.updateReferralSettings(reward);
      toast.success('Referral reward updated successfully');
      await fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update referral reward');
    } finally {
      setReferralLoading(false);
    }
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...dailyReportEmails];
    newEmails[index] = value;
    setDailyReportEmails(newEmails);
  };

  const handleClearEmail = (index) => {
    const newEmails = [...dailyReportEmails];
    newEmails[index] = '';
    setDailyReportEmails(newEmails);
  };

  const handleSaveDailyReportEmails = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = dailyReportEmails.filter(e => e.trim() !== '');
    
    for (const email of validEmails) {
      if (!emailRegex.test(email.trim())) {
        toast.error(`Invalid email format: ${email}`);
        return;
      }
    }

    setDailyReportLoading(true);
    try {
      const response = await adminAPI.saveDailyReportSettings(validEmails.map(e => e.trim()));
      setDailyReportEnabled(response.data?.enabled || false);
      toast.success('Daily report settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save daily report settings');
    } finally {
      setDailyReportLoading(false);
    }
  };

  const handleSendTestReport = async () => {
    const validEmails = dailyReportEmails.filter(e => e.trim() !== '');
    if (validEmails.length === 0) {
      toast.error('Please add at least one email address first');
      return;
    }

    setSendingTestReport(true);
    try {
      const response = await adminAPI.sendDailyReportNow();
      toast.success(response.data?.message || 'Daily report is being sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test report');
    } finally {
      setSendingTestReport(false);
    }
  };

  if (loading) {
    return <PageSkeleton variant="admin" />;
  }

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="navy-gradient text-white px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <BackButton to="/home-new" className="p-2 hover:bg-white/10 rounded-lg" iconClassName="w-5 h-5 text-white" />
            <h1 className="font-serif text-xl font-semibold">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-400" />
            <span className="text-white/80">Logged in as {user?.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <AdminStatsGrid 
          dashboard={dashboard} 
          hasPermission={hasPermission} 
          onNavigate={navigate} 
        />

        {/* Gold Rate Management */}
        <GoldRateManagement
          dashboard={dashboard}
          overrideRate={overrideRate}
          setOverrideRate={setOverrideRate}
          overrideReason={overrideReason}
          setOverrideReason={setOverrideReason}
          overrideLoading={overrideLoading}
          hasPermission={hasPermission}
          canEdit={canEdit}
          onOverrideRate={handleOverrideRate}
          onClearOverride={handleClearOverride}
        />

        {/* SIP/Autopay Dashboard Card */}
        {hasPermission('transactions') && (
          <SIPDashboardCard
            hasPermission={hasPermission}
            onNavigate={navigate}
          />
        )}

        {/* Referral Settings */}
        {hasPermission('settings') && (
          <ReferralSettings
            referralSettings={referralSettings}
            newReferralReward={newReferralReward}
            setNewReferralReward={setNewReferralReward}
            referralLoading={referralLoading}
            canEdit={canEdit}
            onUpdateReward={handleUpdateReferralReward}
          />
        )}

        {/* Daily Report Settings */}
        {hasPermission('settings') && (
          <DailyReportSettings
            dailyReportEmails={dailyReportEmails}
            dailyReportEnabled={dailyReportEnabled}
            dailyReportLoading={dailyReportLoading}
            sendingTestReport={sendingTestReport}
            onEmailChange={handleEmailChange}
            onClearEmail={handleClearEmail}
            onSave={handleSaveDailyReportEmails}
            onSendTest={handleSendTestReport}
          />
        )}

        {/* Quick Links */}
        <AdminQuickLinks hasPermission={hasPermission} />

        {/* View Only Notice */}
        {!canEdit() && <ViewOnlyNotice />}

        {/* Today's Activity */}
        <TodaysActivity dashboard={dashboard} />

        {/* Danger Zone */}
        {isPrimaryAdmin() && (
          <DangerZone 
            deleteLoading={deleteLoading} 
            onDeleteAllData={handleDeleteAllData} 
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
